import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Building2, FileText, BarChart3, Zap, Euro, 
  CheckCircle, XCircle, Calendar, ExternalLink, Download,
  Plus, FileDown, Loader2, Check, Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { downloadPdf } from '../utils/downloadPdf';

const FicheClient = () => {
  const { siren } = useParams();
  const navigate = useNavigate();
  const { canCreateRapport } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Sélection pour synthèse
  const [selectedTICFE, setSelectedTICFE] = useState([]);
  const [selectedTURPE, setSelectedTURPE] = useState([]);
  const [selectedTICGN, setSelectedTICGN] = useState([]);
  const [generatingSynthese, setGeneratingSynthese] = useState(false);

  useEffect(() => {
    fetchFicheClient();
  }, [siren]);

  const fetchFicheClient = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/entreprises/${siren}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Entreprise non trouvée');
        } else {
          setError('Erreur lors du chargement');
        }
        return;
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '0 €';
    return value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Toggle sélection TICFE
  const toggleTICFE = (id) => {
    setSelectedTICFE(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Toggle sélection TURPE
  const toggleTURPE = (id) => {
    setSelectedTURPE(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Sélectionner tout TICFE
  const selectAllTICFE = () => {
    if (!data) return;
    if (selectedTICFE.length === data.rapports_ticfe.length) {
      setSelectedTICFE([]);
    } else {
      setSelectedTICFE(data.rapports_ticfe.map(r => r.id));
    }
  };

  // Sélectionner tout TURPE
  const selectAllTURPE = () => {
    if (!data) return;
    if (selectedTURPE.length === data.rapports_turpe.length) {
      setSelectedTURPE([]);
    } else {
      setSelectedTURPE(data.rapports_turpe.map(r => r.id));
    }
  };

  // Sélectionner tout TICGN
  const selectAllTICGN = () => {
    if (!data) return;
    const list = data.rapports_ticgn || [];
    if (selectedTICGN.length === list.length) {
      setSelectedTICGN([]);
    } else {
      setSelectedTICGN(list.map(r => r.id));
    }
  };

  const toggleSelectTICGN = (id) => {
    setSelectedTICGN(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Supprimer un rapport TICFE
  const deleteRapportTICFE = async (rapportId) => {
    if (!window.confirm('Supprimer ce rapport TICFE ?\n\nCette action est irréversible.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/rapports/${rapportId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        // Rafraîchir les données
        fetchFicheClient();
        // Retirer de la sélection si sélectionné
        setSelectedTICFE(prev => prev.filter(id => id !== rapportId));
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression du rapport');
    }
  };

  // Supprimer un rapport TURPE
  const deleteRapportTURPE = async (rapportId) => {
    if (!window.confirm('Supprimer ce rapport TURPE ?\n\nCette action est irréversible.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/${rapportId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        // Rafraîchir les données
        fetchFicheClient();
        // Retirer de la sélection si sélectionné
        setSelectedTURPE(prev => prev.filter(id => id !== rapportId));
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression du rapport');
    }
  };

  // Générer PDF de synthèse
  const genererSynthesePDF = async () => {
    if (selectedTICFE.length === 0 && selectedTURPE.length === 0 && selectedTICGN.length === 0) {
      alert('Veuillez sélectionner au moins un rapport');
      return;
    }

    // Détection des commerciaux distincts impliqués dans les rapports sélectionnés
    const allSelected = [
      ...(data?.rapports_ticfe || []).filter(r => selectedTICFE.includes(r.id)),
      ...(data?.rapports_turpe || []).filter(r => selectedTURPE.includes(r.id)),
      ...(data?.rapports_ticgn || []).filter(r => selectedTICGN.includes(r.id)),
    ];
    const uniqueCommercialIds = [...new Set(allSelected.map(r => r.commercial_id).filter(Boolean))];

    let commercial_id_override = null;
    if (uniqueCommercialIds.length > 1) {
      // Récupération des noms pour affichage
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commerciaux`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const allComs = resp.ok ? await resp.json() : [];
        const choices = uniqueCommercialIds.map((id, idx) => {
          const c = (allComs.items || allComs || []).find(x => String(x.id) === String(id));
          const label = c ? `${c.prenom || ''} ${c.nom || ''}${c.agence ? ` — ${c.agence}` : ''}` : `Commercial ${id}`;
          return { idx: idx + 1, id, label };
        });
        const msg = `Plusieurs conseillers sont liés aux rapports sélectionnés.\n\nQuel conseiller doit apparaître sur la carte contact du PDF ?\n\n` +
          choices.map(c => `${c.idx}. ${c.label}`).join('\n') +
          `\n\nTapez le numéro :`;
        const pick = window.prompt(msg, '1');
        if (pick === null) return; // annulation
        const idx = parseInt(pick, 10);
        const chosen = choices.find(c => c.idx === idx);
        if (!chosen) {
          alert('Choix invalide, génération annulée.');
          return;
        }
        commercial_id_override = chosen.id;
      } catch (e) {
        console.error('Erreur picker commercial', e);
      }
    }

    setGeneratingSynthese(true);
    try {
      await downloadPdf({
        url: `/api/synthese/pdf`,
        method: 'POST',
        body: {
          siren: siren,
          rapport_ids_ticfe: selectedTICFE,
          rapport_ids_turpe: selectedTURPE,
          rapport_ids_ticgn: selectedTICGN,
          commercial_id_override,
        },
        filename: `synthese_${data.entreprise.nom.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
      });
    } catch { /* toast géré par l'utilitaire */ } finally {
      setGeneratingSynthese(false);
    }
  };

  // Calculer les économies des rapports sélectionnés
  const getSelectedEconomies = () => {
    if (!data) return { ticfe: 0, turpe: 0, total: 0 };
    
    const ecoTICFE = data.rapports_ticfe
      .filter(r => selectedTICFE.includes(r.id))
      .reduce((sum, r) => {
        return sum + (r.economies_attestation_total || 0) + 
          Object.values(r.economies_remboursement || {}).reduce((s, v) => s + v, 0);
      }, 0);
    
    const ecoTURPE = data.rapports_turpe
      .filter(r => selectedTURPE.includes(r.id))
      .reduce((sum, r) => sum + (r.economies || 0), 0);
    
    const ecoTICGN = (data.rapports_ticgn || [])
      .filter(r => selectedTICGN.includes(r.id))
      .reduce((sum, r) => {
        return sum + (r.economies_attestation_total || 0) +
          Object.values(r.economies_remboursement || {}).reduce((s, v) => s + v, 0);
      }, 0);
    
    return { ticfe: ecoTICFE, turpe: ecoTURPE, ticgn: ecoTICGN, total: ecoTICFE + ecoTURPE + ecoTICGN };
  };

  const selectedEconomies = getSelectedEconomies();
  const hasSelection = selectedTICFE.length > 0 || selectedTURPE.length > 0 || selectedTICGN.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#397DFF]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-16 h-16 text-[#BFC6CC] mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-[#191D1E] mb-2">{error}</h2>
        <p className="text-[#687175] mb-4">SIREN recherché : {siren}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="text-[#397DFF] hover:text-[#4672B6] font-medium"
        >
          Retour au tableau de bord
        </button>
      </div>
    );
  }

  const { entreprise, stats, rapports_ticfe, rapports_turpe, rapports_ticgn = [] } = data;

  return (
    <div data-testid="fiche-client-page" className="pb-24">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard?tab=entreprise')}
          className="flex items-center gap-2 text-[#687175] hover:text-[#191D1E] mb-4"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au tableau de bord
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-heading font-semibold text-[#191D1E] tracking-tight" data-testid="company-name">
              {entreprise.nom || 'Entreprise'}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-[#687175]">
              <span className="font-mono" data-testid="company-siren">SIREN : {entreprise.siren}</span>
              {entreprise.siret && (
                <span className="font-mono" data-testid="company-siret">SIRET : {entreprise.siret}</span>
              )}
            </div>
          </div>
          
          {/* Boutons d'action rapide */}
          <div className="flex gap-3">
            <Link
              to={`/rapports/create?siren=${entreprise.siren}&nom=${encodeURIComponent(entreprise.nom || '')}`}
              className="flex items-center gap-2 bg-white border border-[#BFC6CC] hover:bg-[#F5F6F9] text-[#191D1E] font-medium py-2.5 px-4 rounded-lg text-sm"
              data-testid="new-ticfe-analysis"
            >
              <Plus className="w-4 h-4" />
              <Zap className="w-4 h-4 text-[#F9C80E]" />
              Nouvelle analyse TICFE
            </Link>
            <Link
              to={`/analyses/turpe/create?siren=${entreprise.siren}&nom=${encodeURIComponent(entreprise.nom || '')}`}
              className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium py-2.5 px-4 rounded-lg text-sm"
              data-testid="new-turpe-analysis"
            >
              <Plus className="w-4 h-4" />
              <BarChart3 className="w-4 h-4" />
              Nouvelle analyse TURPE
            </Link>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-5 gap-4 mb-8" data-testid="kpi-cards">
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-[#687175] mb-1">
            <Zap className="w-4 h-4 text-[#F9C80E]" />
            Rapports TICFE
          </div>
          <div className="text-2xl font-bold text-[#191D1E]" data-testid="kpi-ticfe-count">
            {stats.nb_rapports_ticfe}
          </div>
          <div className="text-xs text-[#687175] mt-1">
            {stats.nb_eligible} éligible{stats.nb_eligible > 1 ? 's' : ''}
          </div>
        </div>
        
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-[#687175] mb-1">
            <BarChart3 className="w-4 h-4 text-[#397DFF]" />
            Rapports TURPE
          </div>
          <div className="text-2xl font-bold text-[#191D1E]" data-testid="kpi-turpe-count">
            {stats.nb_rapports_turpe}
          </div>
        </div>

        <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-[#687175] mb-1">
            <svg className="w-4 h-4 text-[#FF6B35]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
            Rapports TICGN
          </div>
          <div className="text-2xl font-bold text-[#191D1E]" data-testid="kpi-ticgn-count">
            {stats.nb_rapports_ticgn || 0}
          </div>
          <div className="text-xs text-[#687175] mt-1">
            {stats.nb_ticgn_eligible || 0} éligible{(stats.nb_ticgn_eligible || 0) > 1 ? 's' : ''}
          </div>
        </div>
        
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
          <div className="text-sm text-[#687175] mb-1">Économies TICFE</div>
          <div className="text-2xl font-bold text-[#F9C80E]" data-testid="kpi-ticfe-savings">
            {formatCurrency(stats.economies_ticfe)}
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-[#10B981] to-[#059669] rounded-lg p-4 text-white">
          <div className="text-sm text-emerald-100 mb-1">Total Économies</div>
          <div className="text-2xl font-bold" data-testid="kpi-total-savings">
            {formatCurrency(stats.economies_total)}
          </div>
          <div className="text-xs text-emerald-100 mt-1">
            TURPE : {formatCurrency(stats.economies_turpe)} | TICGN : {formatCurrency(stats.economies_ticgn || 0)}
          </div>
        </div>
      </div>

      {/* Section Rapports TICFE */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg mb-6" data-testid="ticfe-section">
        <div className="px-6 py-4 border-b border-[#BFC6CC] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#F9C80E]" />
            <h2 className="font-semibold text-[#191D1E]">Rapports TICFE</h2>
            <span className="px-2 py-0.5 bg-[#FFF9E6] text-[#F9C80E] rounded text-xs font-medium">
              {stats.nb_rapports_ticfe}
            </span>
          </div>
          {rapports_ticfe.length > 0 && (
            <button
              onClick={selectAllTICFE}
              className="text-sm text-[#397DFF] hover:text-[#4672B6]"
            >
              {selectedTICFE.length === rapports_ticfe.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          )}
        </div>
        
        {rapports_ticfe.length === 0 ? (
          <div className="p-8 text-center text-[#687175]">
            <FileText className="w-12 h-12 mx-auto mb-3 text-[#BFC6CC]" />
            <p>Aucun rapport TICFE pour cette entreprise</p>
            {canCreateRapport && canCreateRapport() && (
              <Link 
                to={`/rapports/create?siren=${entreprise.siren}&nom=${encodeURIComponent(entreprise.nom || '')}`}
                className="text-[#397DFF] hover:underline mt-2 inline-block"
              >
                Créer un rapport TICFE
              </Link>
            )}
          </div>
        ) : (
          <table className="w-full" data-testid="ticfe-table">
            <thead className="bg-[#F5F6F9] text-xs text-[#687175] uppercase">
              <tr>
                <th className="w-10 px-4 py-3"></th>
                <th className="text-left px-6 py-3">Date</th>
                <th className="text-left px-6 py-3">PDL(s)</th>
                <th className="text-center px-6 py-3">Éligibilité</th>
                <th className="text-right px-6 py-3">Économies</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rapports_ticfe.map((rapport, idx) => {
                const pdlCount = rapport.pdls?.length || rapport.pdls_data?.length || 0;
                const economiesTotal = (rapport.economies_attestation_total || 0) + 
                  Object.values(rapport.economies_remboursement || {}).reduce((s, v) => s + v, 0);
                const isSelected = selectedTICFE.includes(rapport.id);
                
                return (
                  <tr key={rapport.id || idx} className={`border-t border-[#BFC6CC] hover:bg-[#F5F6F9] ${isSelected ? 'bg-[#E8F4FD]' : ''}`}>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => toggleTICFE(rapport.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected 
                            ? 'bg-[#397DFF] border-[#397DFF] text-white' 
                            : 'border-[#BFC6CC] hover:border-[#397DFF]'
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3" />}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-[#687175]">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(rapport.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-[#191D1E]">
                        {pdlCount} PDL{pdlCount > 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        rapport.eligibilite_globale === 'ELIGIBLE'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {rapport.eligibilite_globale === 'ELIGIBLE' 
                          ? <CheckCircle className="w-3 h-3" />
                          : <XCircle className="w-3 h-3" />
                        }
                        {rapport.eligibilite_globale}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-[#10B981]">
                      {formatCurrency(economiesTotal)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/rapports/ticfe/${rapport.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#397DFF] hover:text-[#4672B6] text-sm flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Consulter
                        </Link>
                        {canCreateRapport && canCreateRapport() && (
                          <button
                            onClick={() => deleteRapportTICFE(rapport.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Supprimer ce rapport"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Section Rapports TURPE */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg mb-6" data-testid="turpe-section">
        <div className="px-6 py-4 border-b border-[#BFC6CC] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#397DFF]" />
            <h2 className="font-semibold text-[#191D1E]">Rapports TURPE</h2>
            <span className="px-2 py-0.5 bg-[#E8F4FD] text-[#397DFF] rounded text-xs font-medium">
              {stats.nb_rapports_turpe}
            </span>
          </div>
          {rapports_turpe.length > 0 && (
            <button
              onClick={selectAllTURPE}
              className="text-sm text-[#397DFF] hover:text-[#4672B6]"
            >
              {selectedTURPE.length === rapports_turpe.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          )}
        </div>
        
        {rapports_turpe.length === 0 ? (
          <div className="p-8 text-center text-[#687175]">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 text-[#BFC6CC]" />
            <p>Aucun rapport TURPE pour cette entreprise</p>
            {canCreateRapport && canCreateRapport() && (
              <Link 
                to={`/analyses/turpe/create?siren=${entreprise.siren}&nom=${encodeURIComponent(entreprise.nom || '')}`}
                className="text-[#397DFF] hover:underline mt-2 inline-block"
              >
                Créer une analyse TURPE
              </Link>
            )}
          </div>
        ) : (
          <table className="w-full" data-testid="turpe-table">
            <thead className="bg-[#F5F6F9] text-xs text-[#687175] uppercase">
              <tr>
                <th className="w-10 px-4 py-3"></th>
                <th className="text-left px-6 py-3">Date</th>
                <th className="text-left px-6 py-3">PDL / Sites</th>
                <th className="text-left px-6 py-3">Formule</th>
                <th className="text-right px-6 py-3">Économies</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rapports_turpe.map((rapport, idx) => {
                const isMulti = rapport.pdls && rapport.pdls.length > 0;
                const pdlInfo = isMulti 
                  ? `${rapport.pdls.length} sites` 
                  : (rapport.pdl?.numero?.slice(-6) || '-');
                const formule = isMulti 
                  ? 'Multi-sites' 
                  : (rapport.formuleActuelle || '-');
                const isSelected = selectedTURPE.includes(rapport.id);
                
                return (
                  <tr key={rapport.id || idx} className={`border-t border-[#BFC6CC] hover:bg-[#F5F6F9] ${isSelected ? 'bg-[#E8F4FD]' : ''}`}>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => toggleTURPE(rapport.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected 
                            ? 'bg-[#397DFF] border-[#397DFF] text-white' 
                            : 'border-[#BFC6CC] hover:border-[#397DFF]'
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3" />}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-[#687175]">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(rapport.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-sm text-[#191D1E]">
                      {pdlInfo}
                    </td>
                    <td className="px-6 py-4 text-[#687175]">
                      {formule}
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-[#10B981]">
                      {formatCurrency(rapport.economies)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={isMulti ? `/rapports/turpe/multi/${rapport.id}` : `/rapports/turpe/${rapport.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#397DFF] hover:text-[#4672B6] text-sm flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Consulter
                        </Link>
                        {canCreateRapport && canCreateRapport() && (
                          <button
                            onClick={() => deleteRapportTURPE(rapport.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Supprimer ce rapport"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Section Rapports TICGN */}
      {rapports_ticgn.length > 0 && (
        <div className="bg-white border border-[#BFC6CC] rounded-lg mb-6" data-testid="ticgn-section">
          <div className="px-6 py-4 border-b border-[#BFC6CC] flex items-center gap-2">
            <svg className="w-5 h-5 text-[#FF6B35]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
            <h2 className="font-semibold text-[#191D1E]">Rapports TICGN</h2>
            <span className="px-2 py-0.5 bg-orange-100 text-[#FF6B35] rounded text-xs font-medium">
              {rapports_ticgn.length}
            </span>
            <button
              onClick={selectAllTICGN}
              className="ml-auto text-xs text-[#397DFF] hover:text-[#4672B6] font-medium"
              data-testid="select-all-ticgn"
            >
              {selectedTICGN.length === rapports_ticgn.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          </div>
          <table className="w-full" data-testid="ticgn-table">
            <thead className="bg-[#F5F6F9] text-xs text-[#687175] uppercase">
              <tr>
                <th className="px-4 py-3 w-10"></th>
                <th className="text-left px-6 py-3">Date</th>
                <th className="text-left px-6 py-3">PCE(s)</th>
                <th className="text-center px-6 py-3">Éligibilité</th>
                <th className="text-right px-6 py-3">Économies</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rapports_ticgn.map((rapport, idx) => {
                const pceCount = rapport.pces_data?.length || 0;
                const economiesTotal = (rapport.economies_attestation_total || 0) + 
                  Object.values(rapport.economies_remboursement || {}).reduce((s, v) => s + v, 0);
                const isSelected = selectedTICGN.includes(rapport.id);
                return (
                  <tr key={rapport.id || idx} className={`border-t border-[#BFC6CC] transition-colors ${isSelected ? 'bg-orange-50' : 'hover:bg-[#F5F6F9]'}`}>
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectTICGN(rapport.id)}
                        className="w-4 h-4 accent-[#FF6B35] cursor-pointer"
                        data-testid={`select-ticgn-${rapport.id}`}
                      />
                    </td>
                    <td className="px-6 py-4 text-[#687175]">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(rapport.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#191D1E]">
                      {pceCount} PCE{pceCount > 1 ? 's' : ''}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        rapport.eligibilite_globale === 'ELIGIBLE'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {rapport.eligibilite_globale === 'ELIGIBLE' 
                          ? <CheckCircle className="w-3 h-3" />
                          : <XCircle className="w-3 h-3" />
                        }
                        {rapport.eligibilite_globale}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-[#10B981]">
                      {formatCurrency(economiesTotal)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/rapports/ticgn/${rapport.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#397DFF] hover:text-[#4672B6] text-sm flex items-center gap-1 justify-end"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Consulter
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Barre d'action flottante pour synthèse */}
      {hasSelection && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#191D1E] text-white rounded-xl shadow-2xl px-6 py-4 flex items-center gap-6 z-50" data-testid="synthesis-action-bar">
          <div className="flex items-center gap-4">
            <div className="text-sm">
              <span className="text-[#BFC6CC]">Sélection :</span>
              {selectedTICFE.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-[#F9C80E] text-[#191D1E] rounded text-xs font-medium">
                  {selectedTICFE.length} TICFE
                </span>
              )}
              {selectedTURPE.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-[#397DFF] text-white rounded text-xs font-medium">
                  {selectedTURPE.length} TURPE
                </span>
              )}
              {selectedTICGN.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-[#FF6B35] text-white rounded text-xs font-medium">
                  {selectedTICGN.length} TICGN
                </span>
              )}
            </div>
            <div className="h-8 w-px bg-[#687175]"></div>
            <div>
              <div className="text-xs text-[#BFC6CC]">Économies sélectionnées</div>
              <div className="text-lg font-bold text-[#10B981]">{formatCurrency(selectedEconomies.total)}</div>
            </div>
          </div>
          
          <button
            onClick={genererSynthesePDF}
            disabled={generatingSynthese}
            className="flex items-center gap-2 bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-medium py-3 px-6 rounded-lg disabled:opacity-50"
            data-testid="generate-synthesis-btn"
          >
            {generatingSynthese ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Génération...
              </>
            ) : (
              <>
                <FileDown className="w-5 h-5" />
                Générer Synthèse PDF
              </>
            )}
          </button>
          
          <button
            onClick={() => { setSelectedTICFE([]); setSelectedTURPE([]); setSelectedTICGN([]); }}
            className="text-[#BFC6CC] hover:text-white text-sm"
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  );
};

export default FicheClient;
